import React, { useState, useContext } from "react";
import { db } from "../firebase";
import { deleteDoc, doc } from "firebase/firestore";
import "./Blog.css";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { BlogContext } from "../context/BlogContext";

function Blog() {
  const PAGE_SIZE = 5;
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  // Using both contexts
  const { user, logout } = useContext(AuthContext);
  const { blogs, loading, fetchNextPage, hasMore, deleteBlogFromState } = useContext(BlogContext);

  const totalLoadedPages = Math.ceil((blogs.length || 0) / PAGE_SIZE);

  const handleNext = async () => {
    if (page + 1 > totalLoadedPages && hasMore) {
      await fetchNextPage();
    }
    setPage(page + 1);
  };

  const handlePrev = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleAddBlogClick = () => {
    if (user) {
      navigate("/blog-add");
    } else {
      Swal.fire({
        icon: "info",
        title: "Admin Login Required",
        text: "Please login as admin to add a blog.",
        confirmButtonText: "Go to Admin Login",
      }).then(() => navigate("/admin-login"));
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d32f2f",
      cancelButtonColor: "#888",
      confirmButtonText: "Yes, delete it!",
    });
    if (!result.isConfirmed) return;
    try {
      await deleteDoc(doc(db, "blogs", id));
      deleteBlogFromState(id);
      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "Blog has been deleted.",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Failed to delete blog",
        text: err.message,
      });
    }
  };

  const createSnippet = (htmlString, maxLength) => {
    if (!htmlString) return "";
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlString;
    if (tempDiv.textContent.length <= maxLength) {
      return htmlString;
    }
    let truncatedHtml = "";
    let charCount = 0;
    const openTags = [];
    const parts = htmlString.split(/(<[^>]+>)/);
    for (const part of parts) {
      if (part.startsWith("<")) {
        if (part.startsWith("</")) {
          openTags.pop();
        } else if (!part.endsWith("/>")) {
          const tagName = part.match(/<([a-zA-Z0-9]+)/);
          if (tagName) {
            openTags.push(tagName[1]);
          }
        }
        truncatedHtml += part;
      } else {
        if (charCount < maxLength) {
          const remaining = maxLength - charCount;
          if (part.length > remaining) {
            truncatedHtml += part.substring(0, remaining);
            charCount = maxLength;
          } else {
            truncatedHtml += part;
            charCount += part.length;
          }
        }
      }
      if (charCount >= maxLength) break;
    }
    truncatedHtml += "...";
    while (openTags.length > 0) {
      truncatedHtml += `</${openTags.pop()}>`;
    }
    return truncatedHtml;
  };

  const paginatedBlogs = blogs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="blog-container">
      {/* ADD/LOGOUT BUTTONS ARE BACK */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <h2>Recent Blogs</h2>
        <div className="admin-buttons">
          {user && (
            <>
              <button className="button" onClick={handleAddBlogClick}>
                Add Blog
              </button>
              <button
                className="button"
                style={{ background: "#d32f2f", marginLeft: "10px" }}
                onClick={logout}
              >
                Logout
              </button>
            </>
          )}
        </div>
      </div>

      {/* SEARCH HAS BEEN REMOVED */}

      {loading && blogs.length === 0 ? (
        <p>Loading...</p>
      ) : (
        <ul className="blog-list">
          {paginatedBlogs.map((blog) => (
            <li
              key={blog.id}
              className="blog-item"
              style={{
                display: "grid",
                gridTemplateColumns: "auto 1fr",
                gap: "16px",
                alignItems: "center",
              }}
            >
              <div style={{ width: "80px", aspectRatio: "1 / 1" }}>
                <img
                  src={blog.imageUrl || "https://via.placeholder.com/80"}
                  alt="blog"
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: 8,
                    objectFit: "cover",
                  }}
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <h3>{blog.title}</h3>
                <div
                  className="blog-item-snippet"
                  dangerouslySetInnerHTML={{
                    __html: createSnippet(blog.content, 120),
                  }}
                  style={{ flexGrow: 1 }}
                />
                <div
                  style={{
                    fontSize: 14,
                    color: "#555",
                    marginTop: 5,
                    marginBottom: 5,
                  }}
                >
                  {blog.date && blog.date.seconds
                    ? new Date(
                      blog.date.seconds * 1000
                    ).toLocaleDateString()
                    : ""}
                  <span> </span>
                  {blog.date && blog.date.seconds
                    ? new Date(
                      blog.date.seconds * 1000
                    ).toLocaleTimeString()
                    : ""}
                </div>
                <div>
                  <button
                    className="button"
                    style={{ marginLeft: 0, fontSize: 14 }}
                    onClick={() => navigate(`/blog/${blog.id}`)}
                  >
                    Read More
                  </button>
                  {user && (
                    <button
                      className="button"
                      style={{
                        marginLeft: 8,
                        background: "#d32f2f",
                        fontSize: 14,
                      }}
                      onClick={() => handleDelete(blog.id)}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
      {blogs.length > 0 && (
        <div className="pagination">
          <button
            onClick={handlePrev}
            className="button"
            disabled={page === 1}
          >
            <span className="pagination-arrow">&lt;</span>
            <span className="pagination-text"> Previous</span>
          </button>
          <span>Page {page}</span>
          <button
            onClick={handleNext}
            className="button"
            disabled={page >= totalLoadedPages && !hasMore}
          >
            <span className="pagination-text">Next </span>
            <span className="pagination-arrow">&gt;</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default Blog;